import { MoreHorizontal, CheckCircle, Clock, Plus, Edit, Trash2, UserPlus, BarChart3, Clock as ClockIcon, Download, Eye } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { LIST_TOOLBAR_BTN, LIST_TOOLBAR_ICON, LIST_TOOLBAR_ROW } from "../../constants/listToolbarButtons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect, useMemo, useCallback } from "react";
import { ApiService } from "../../services/api";
import checkPermission from "../../utils/permission";
import ConfirmModal from "../../components/modals/ConfirmModal";
import { useModal } from "../../hooks/useModal";
import { usePanels } from "../../contexts/PanelContext.jsx";
import Modal from "../../components/ui/Modal";
import DepartmentTreeSelect from "../e-education/components/selectors/DepartmentTreeSelect";

type ExportDepartmentNode = {
  id: string;
  name: string;
  parentId?: string | null;
  hasChildren?: boolean;
};

type ExportDepartmentCount = {
  direct: number;
  total: number;
};

const normalizeExportDepartments = (departmentData: any): ExportDepartmentNode[] =>
  (Array.isArray(departmentData) ? departmentData : [])
    .filter((department: any) => department?.id && !department?.deletedAt)
    .map((department: any) => ({
      id: String(department.id),
      name: String(department.name || department.title || ""),
      parentId: department.parentId ? String(department.parentId) : null,
      hasChildren: Boolean(department.hasChildren),
    }));

const fetchExportDepartmentsForSurvey = async (surveyId: string): Promise<ExportDepartmentNode[]> => {
  const participantsResponse = await ApiService.getSurveyParticipants(surveyId);
  const participantRows = Array.isArray(participantsResponse?.data) ? participantsResponse.data : [];

  const participantDepartments = participantRows
    .filter((participant: any) => participant?.type === "DEPARTMENT" && participant?.department?.id)
    .map((participant: any) => participant.department);

  const rootDepartments = normalizeExportDepartments(participantDepartments);
  const allDepartmentsMap = new Map(rootDepartments.map((department) => [department.id, department]));

  const subtreeResponses = await Promise.all(
    rootDepartments
      .filter((department) => department.hasChildren)
      .map((department) => ApiService.getAllDepartments(department.id))
  );

  subtreeResponses.forEach((response) => {
    const subtreeDepartments = normalizeExportDepartments(response?.data || response || []);
    subtreeDepartments.forEach((department) => {
      allDepartmentsMap.set(department.id, department);
    });
  });

  return Array.from(allDepartmentsMap.values());
};

export default function SurveyPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showMyAnswersModal, setShowMyAnswersModal] = useState(false);
  const [myAnswersLoading, setMyAnswersLoading] = useState(false);
  const [myAnswersData, setMyAnswersData] = useState<any>(null);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportSurvey, setExportSurvey] = useState<any>(null);
  const [exportDepartmentIds, setExportDepartmentIds] = useState<string[]>([]);
  const [allDepartments, setAllDepartments] = useState<ExportDepartmentNode[]>([]);
  const [exportSubmitting, setExportSubmitting] = useState(false);
  const [exportDepartmentParticipantCounts, setExportDepartmentParticipantCounts] = useState<Record<string, ExportDepartmentCount>>({});
  const [exportDepartmentCountsLoading, setExportDepartmentCountsLoading] = useState(false);
  const { showAlert, showConfirm } = useModal();
  const { mainContentRightInsetActive } = usePanels();

  const canListSurveys = checkPermission("survey_list");
  const canViewResults = checkPermission("survey_update");
  const canAddSurvey = checkPermission("survey_add");
  const canUpdateSurvey = checkPermission("survey_update");
  const canDeleteSurvey = checkPermission("survey_delete");

  useEffect(() => {
    const shouldRefresh = localStorage.getItem("surveyListShouldRefresh");
    if (shouldRefresh) {
      localStorage.removeItem("surveyListShouldRefresh");
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".dropdown-container")) {
        setDropdownOpen(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const buildExportDepartmentCounts = useCallback(async (surveyId: string, departments: ExportDepartmentNode[]) => {
    const directCounts = await ApiService.getSurveyParticipantDepartmentCounts(surveyId);

    const childrenMap = new Map<string, string[]>();
    departments.forEach((department) => {
      if (!department.parentId) return;
      const siblings = childrenMap.get(department.parentId) || [];
      siblings.push(department.id);
      childrenMap.set(department.parentId, siblings);
    });

    const countWithChildren = (departmentId: string, visited = new Set<string>()): number => {
      if (visited.has(departmentId)) return 0;
      visited.add(departmentId);

      const childIds = childrenMap.get(departmentId) || [];
      return (
        (directCounts[departmentId] || 0) +
        childIds.reduce((total, childId) => total + countWithChildren(childId, visited), 0)
      );
    };

    const aggregatedCounts: Record<string, ExportDepartmentCount> = {};
    departments.forEach((department) => {
      aggregatedCounts[department.id] = {
        direct: directCounts[department.id] || 0,
        total: countWithChildren(department.id),
      };
    });

    Object.keys(directCounts).forEach((departmentId) => {
      if (aggregatedCounts[departmentId] === undefined) {
        aggregatedCounts[departmentId] = {
          direct: directCounts[departmentId],
          total: directCounts[departmentId],
        };
      }
    });

    return aggregatedCounts;
  }, []);

  useEffect(() => {
    if (!showExportModal || !exportSurvey?.id) {
      setAllDepartments([]);
      setExportDepartmentParticipantCounts({});
      setExportDepartmentCountsLoading(false);
      return;
    }

    let mounted = true;

    const loadExportModalData = async () => {
      try {
        const departments = await fetchExportDepartmentsForSurvey(exportSurvey.id);
        if (!mounted) return;

        setAllDepartments(departments);
        setExportDepartmentCountsLoading(true);
        const aggregatedCounts = await buildExportDepartmentCounts(exportSurvey.id, departments);
        if (!mounted) return;

        setExportDepartmentParticipantCounts(aggregatedCounts);
      } catch (error) {
        console.error("Export departman verileri yüklenemedi:", error);
        if (mounted) {
          setAllDepartments([]);
          setExportDepartmentParticipantCounts({});
        }
      } finally {
        if (mounted) {
          setExportDepartmentCountsLoading(false);
        }
      }
    };

    loadExportModalData();

    return () => {
      mounted = false;
    };
  }, [showExportModal, exportSurvey?.id, buildExportDepartmentCounts]);

  const handleDropdownToggle = useCallback(
    (surveyId: string) => {
      const newState = dropdownOpen === surveyId ? null : surveyId;
      setDropdownOpen(newState);
    },
    [dropdownOpen]
  );

  const handleEdit = useCallback(
    (survey: any) => {
      setDropdownOpen(null);
      navigate(`/surveys/edit/${survey.id}`);
    },
    [navigate]
  );

  const handleParticipate = useCallback(
    (survey: any) => {
      setDropdownOpen(null);
      navigate(`/surveys/participate/${survey.id}`);
    },
    [navigate]
  );

  const handleViewResults = useCallback(
    (survey: any) => {
      setDropdownOpen(null);
      navigate(`/surveys/results/${survey.id}`);
    },
    [navigate]
  );

  const formatFileSize = (size?: number) => {
    if (!size && size !== 0) return "-";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const renderMyAnswer = (item: any) => {
    const fileMeta = item?.answerMedia || item?.answer;
    if (fileMeta && typeof fileMeta === "object" && (fileMeta.url || fileMeta.fileName || fileMeta.name)) {
      const fileUrl = fileMeta.url;
      const fileName = fileMeta.fileName || fileMeta.name || "Dosya";
      const fileSize = formatFileSize(fileMeta.size);
      if (fileUrl) {
        return (
          <a href={fileUrl} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline break-all">
            {fileName} ({fileSize})
          </a>
        );
      }
      return `${fileName} (${fileSize})`;
    }
    if (Array.isArray(item?.answer)) return item.answer.join(", ");
    return String(item?.answer ?? "Cevapsız");
  };

  const handleViewMyAnswers = useCallback(async (survey: any) => {
    setDropdownOpen(null);
    setShowMyAnswersModal(true);
    setMyAnswersLoading(true);
    setMyAnswersData(null);
    try {
      const res = await ApiService.getMySurveyAnswers(survey.id);
      setMyAnswersData(res);
    } catch (error: any) {
      setShowMyAnswersModal(false);
      await showAlert({
        title: "Hata",
        message: error.message || "Cevaplarınız alınamadı.",
        type: "error",
      });
    } finally {
      setMyAnswersLoading(false);
    }
  }, [showAlert]);

  const handleDeleteClick = useCallback((survey: any) => {
    setSelectedSurvey(survey);
    setShowDeleteModal(true);
    setDropdownOpen(null);
  }, []);

  const handleExportResults = useCallback((survey: any) => {
    setDropdownOpen(null);
    setExportSurvey(survey);
    setExportDepartmentIds([]);
    setShowExportModal(true);
  }, []);

  const handleExportConfirm = useCallback(async () => {
    if (!exportSurvey || exportDepartmentIds.length === 0) return;

    setExportSubmitting(true);
    try {
      const blob = await ApiService.exportSurveyResults(exportSurvey.id, exportDepartmentIds);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');

      link.href = url;
      link.download = `anket_sonuclari_${exportSurvey.title?.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ ]/g, '').substring(0, 30) || 'anket'}_${new Date().toISOString().split('T')[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      setShowExportModal(false);
      setExportSurvey(null);
      setExportDepartmentIds([]);

      await showAlert({
        title: 'Başarılı',
        message: 'Anket sonuçları başarıyla dışa aktarıldı.',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Dışa aktarma hatası:', error);

      await showAlert({
        title: 'Hata',
        message: error.message || 'Anket sonuçları dışa aktarılırken hata oluştu.',
        type: 'error',
      });
    } finally {
      setExportSubmitting(false);
    }
  }, [exportSurvey, exportDepartmentIds, showAlert]);

  const handleDeleteConfirm = async () => {
    if (!selectedSurvey) return;

    setIsDeleting(true);

    try {
      await ApiService.deleteSurvey(selectedSurvey.id);

      setShowDeleteModal(false);
      setSelectedSurvey(null);

      await showAlert({
        title: "Başarılı",
        message: "Anket başarıyla silindi.",
        type: "success",
      });
      window.location.reload();
    } catch (error: any) {
      console.error("Anket silme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Anket silinirken hata oluştu.",
        type: "error",
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const refreshSurveys = () => {
    window.location.reload();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getReviewStatusBadge = (survey: any) => {
    const reviewStatus = survey?.userReviewStatus;

    if (
      reviewStatus !== "APPROVED" &&
      reviewStatus !== "REJECTED" &&
      !(survey?.isActionEnabled && survey?.userParticipationStatus === "COMPLETED")
    ) {
      return null;
    }

    const config =
      reviewStatus === "APPROVED"
        ? {
            label: "Onaylandı",
            className: "border border-emerald-200 bg-emerald-50 text-emerald-700",
          }
        : reviewStatus === "REJECTED"
          ? {
              label: "Reddedildi",
              className: "border border-rose-200 bg-rose-50 text-rose-700",
            }
        : {
            label: "Bekliyor",
            className: "border border-amber-200 bg-amber-50 text-amber-700",
          };

    return (
      <span
        className={`inline-flex items-center rounded-full px-3 py-2 text-xs sm:text-sm font-semibold whitespace-nowrap ${config.className}`}
      >
        {config.label}
      </span>
    );
  };

  const renderItem = useCallback(
    (survey: any, viewMode: string = "list") => {
      if (viewMode === "grid") {
        return (
          <div
            key={survey.id}
            className={`relative border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow flex flex-col h-full ${survey.userParticipationStatus === 'COMPLETED' ? 'bg-gray-50 cursor-default' : 'bg-white cursor-pointer'
              }`}
            onClick={() => {
              if (survey.userParticipationStatus !== 'COMPLETED') {
                handleParticipate(survey);
              }
            }}>
            {(canUpdateSurvey || canDeleteSurvey) && (
              <div className="absolute top-4 right-4 z-10 dropdown-container">
                <button
                  type="button"
                  aria-label="Diğer işlemler"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDropdownToggle(survey.id);
                  }}
                  className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors">
                  <MoreHorizontal className="w-5 h-5" />
                </button>
                {dropdownOpen === survey.id && (
                  <div className="absolute right-0 top-full mt-1 bg-white rounded-lg shadow-lg z-50 border border-gray-200 min-w-[11rem]">
                    <div className="py-1">
                      {canViewResults && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExportResults(survey);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50">
                          <Download className="w-4 h-4" />
                          Dışarı Aktar
                        </button>
                      )}
                      {canUpdateSurvey && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(survey);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                          <Edit className="w-4 h-4" />
                          Düzenle
                        </button>
                      )}
                      {canDeleteSurvey && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteClick(survey);
                          }}
                          className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                          <Trash2 className="w-4 h-4" />
                          Sil
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0">
                {survey.isActive ? (
                  <div className="h-10 w-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                ) : (
                  <div className="h-10 w-10 bg-gray-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-gray-600" />
                  </div>
                )}
              </div>
              <div className={`flex-1 min-w-0 ${canUpdateSurvey || canDeleteSurvey ? "pr-11" : ""}`}>
                <h3 className="text-lg font-semibold text-gray-900 mb-1 break-words">{survey.title}</h3>
                <div className="flex items-center gap-2 flex-wrap mt-2">
                  {survey.isActive ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm">
                      <CheckCircle className="w-3 h-3" />
                      AKTİF
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-sm">
                      <Clock className="w-3 h-3" />
                      PASİF
                    </span>
                  )}
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 line-clamp-2 leading-relaxed mb-4">{survey.description}</p>

            <div className="mt-auto pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                  {survey.user?.media?.url ? (
                    <img
                      src={survey.user.media.url}
                      alt={`${survey.user.firstName} ${survey.user.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-[10px] font-medium">
                      {survey.user?.firstName?.charAt(0)}
                      {survey.user?.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  <div className="font-medium truncate">
                    {survey.user?.firstName} {survey.user?.lastName}
                  </div>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {formatDate(survey.createdAt)}
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex gap-2 w-full flex-wrap">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      if (survey.userParticipationStatus === 'COMPLETED') {
                        handleViewMyAnswers(survey);
                        return;
                      }
                      handleParticipate(survey);
                    }}
                    className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors flex-1 min-w-0 whitespace-nowrap ${survey.userParticipationStatus === 'COMPLETED'
                      ? 'bg-blue-600 text-white hover:bg-blue-700'
                      : survey.userParticipationStatus === 'IN_PROGRESS'
                        ? 'bg-orange-500 text-white hover:bg-orange-600'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                      }`}>
                    {survey.userParticipationStatus === 'COMPLETED' ? (
                      <><Eye className="w-4 h-4 flex-shrink-0" /><span className="truncate">Cevaplarımı Gör</span></>
                    ) : survey.userParticipationStatus === 'IN_PROGRESS' ? (
                      <><Clock className="w-4 h-4 flex-shrink-0" /><span className="truncate">Devam Et</span></>
                    ) : (
                      <><UserPlus className="w-4 h-4 flex-shrink-0" /><span className="truncate">Katıl</span></>
                    )}
                  </button>
                  {survey.userParticipationStatus === 'COMPLETED' && getReviewStatusBadge(survey)}
                  {canViewResults && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewResults(survey);
                      }}
                      className="flex items-center justify-center gap-1.5 px-3 py-2 bg-white border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex-1 min-w-0 whitespace-nowrap">
                      <BarChart3 className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">Sonuçlar</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      }

      return (
        <div
          key={survey.id}
          className={`border border-gray-200 rounded-lg p-3 sm:p-4 hover:shadow-md transition-shadow mb-2 ${survey.userParticipationStatus === 'COMPLETED' ? 'bg-gray-50 cursor-default' : 'bg-white cursor-pointer'
            } ${dropdownOpen === survey.id ? 'relative z-20' : 'relative z-0'}`}
          onClick={() => {
            if (survey.userParticipationStatus !== 'COMPLETED') {
              handleParticipate(survey);
            }
          }}>
          <div className="flex flex-col lg:grid lg:grid-cols-12 gap-3 lg:gap-3 lg:items-center">
            <div className="lg:col-span-3 flex items-start gap-3 min-w-0">
              <div className="flex-shrink-0">
                {survey.isActive ? (
                  <div className="h-8 w-8 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                  </div>
                ) : (
                  <div className="h-8 w-8 bg-gray-100 rounded-full flex items-center justify-center">
                    <Clock className="h-4 w-4 text-gray-600" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900 break-words mb-1">{survey.title}</h3>
                <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 lg:line-clamp-1 break-words whitespace-normal">{survey.description}</p>
              </div>
            </div>

            <div className="lg:col-span-2 min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center overflow-hidden">
                  {survey.user?.media?.url ? (
                    <img
                      src={survey.user.media.url}
                      alt={`${survey.user.firstName} ${survey.user.lastName}`}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-white text-[10px] font-medium">
                      {survey.user?.firstName?.charAt(0)}
                      {survey.user?.lastName?.charAt(0)}
                    </span>
                  )}
                </div>
                <div className="text-xs text-gray-600">
                  <div className="font-medium truncate">
                    {survey.user?.firstName} {survey.user?.lastName}
                  </div>
                  <div className="flex items-center gap-1">
                    <ClockIcon className="w-3 h-3" />
                    {formatDate(survey.createdAt)}
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-2 flex justify-start min-w-0">
              {survey.isActive ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-green-500 to-green-600 text-white shadow-sm max-w-full truncate">
                  <CheckCircle className="w-3 h-3 flex-shrink-0" />
                  <span className="inline md:hidden lg:inline">AKTİF</span>
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-gray-500 to-gray-600 text-white shadow-sm max-w-full truncate">
                  <Clock className="w-3 h-3 flex-shrink-0" />
                  <span className="inline md:hidden lg:inline">PASİF</span>
                </span>
              )}
            </div>

            <div className="lg:col-span-5 flex items-center justify-start lg:justify-end gap-2 flex-wrap flex-shrink-0">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (survey.userParticipationStatus === 'COMPLETED') {
                    handleViewMyAnswers(survey);
                    return;
                  }
                  handleParticipate(survey);
                }}
                className={`inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap ${survey.userParticipationStatus === 'COMPLETED'
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : survey.userParticipationStatus === 'IN_PROGRESS'
                    ? 'bg-orange-500 text-white hover:bg-orange-600'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                  }`}>
                {survey.userParticipationStatus === 'COMPLETED' ? (
                  <><Eye className="w-4 h-4" /> Cevaplarımı Gör</>
                ) : survey.userParticipationStatus === 'IN_PROGRESS' ? (
                  <><Clock className="w-4 h-4" /> Devam Et</>
                ) : (
                  <><UserPlus className="w-4 h-4" /> Katıl</>
                )}
              </button>
              {survey.userParticipationStatus === 'COMPLETED' && getReviewStatusBadge(survey)}
              {canViewResults && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewResults(survey);
                  }}
                  className="inline-flex items-center gap-1.5 px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors whitespace-nowrap">
                  <BarChart3 className="w-4 h-4" />
                  Sonuçlar
                </button>
              )}
              {(canUpdateSurvey || canDeleteSurvey) && (
                <div className="relative dropdown-container flex-shrink-0">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDropdownToggle(survey.id);
                    }}
                    className="p-1.5 rounded-full hover:bg-gray-100">
                    <MoreHorizontal className="h-4 w-4" />
                  </button>
                  {dropdownOpen === survey.id && (
                    <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-md shadow-lg z-[9999] border border-gray-200">
                      <div className="py-1">
                        {canViewResults && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleExportResults(survey);
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-green-600 hover:bg-green-50">
                            <Download className="w-4 h-4" />
                            Dışarı Aktar
                          </button>
                        )}
                        {canUpdateSurvey && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEdit(survey);
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-blue-600 hover:bg-blue-50">
                            <Edit className="w-4 h-4" />
                            Düzenle
                          </button>
                        )}
                        {canDeleteSurvey && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(survey);
                            }}
                            className="flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                            <Trash2 className="w-4 h-4" />
                            Sil
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      );
    },
    [dropdownOpen, handleDropdownToggle, handleParticipate, handleViewMyAnswers, handleEdit, handleViewResults, handleDeleteClick, handleExportResults, canViewResults, canUpdateSurvey, canDeleteSurvey]
  );

  const surveysActions = useMemo(
    () => (
      <div className={LIST_TOOLBAR_ROW}>
        {canAddSurvey && (
          <button
            onClick={() => navigate("/surveys/new")}
            className={`${LIST_TOOLBAR_BTN} bg-green-600 text-white hover:bg-green-700 border-green-600`}>
            <Plus className={LIST_TOOLBAR_ICON} />
            <span>Yeni Anket</span>
          </button>
        )}
      </div>
    ),
    [canAddSurvey, navigate]
  );

  const surveysTab: TabItemProps = useMemo(
    () => ({
      type: "list",
      title: "Tüm Anketler",
      path: "surveys",
      actions: surveysActions,
      isSearch: true,
      showToggle: true,
      gridClassName: mainContentRightInsetActive
        ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 pb-8 items-stretch auto-rows-fr"
        : "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pb-8 items-stretch auto-rows-fr",
      item: renderItem,
    }),
    [surveysActions, renderItem, mainContentRightInsetActive]
  );

  const tabs = useMemo(() => [surveysTab], [surveysTab]);

  return (
    <>
      <TabPage title="Anketler" tabs={tabs} />

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedSurvey(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Anketi Sil"
        message={`"${selectedSurvey?.title}" adlı anketi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`}
        confirmText="Sil"
        isLoading={isDeleting}
      />

      <Modal
        isOpen={showExportModal}
        onClose={() => {
          if (exportSubmitting) return;
          setShowExportModal(false);
          setExportSurvey(null);
          setExportDepartmentIds([]);
          setExportDepartmentParticipantCounts({});
          setExportDepartmentCountsLoading(false);
        }}
        title={`${exportSurvey?.title || "Anket"} - Dışa Aktar`}
        overflowVisible
        size="md">
        <div className="space-y-4 overflow-visible">
          <div className="text-sm text-gray-600">
            Dışa aktarılacak departmanları seçin. Seçim yapılmadan tüm departmanlar otomatik olarak alınmaz.
          </div>
          <div className="overflow-visible">
            <label className="block text-sm font-medium text-gray-700 mb-2">Departman</label>
            <DepartmentTreeSelect
              value={exportDepartmentIds}
              onChange={(departmentIds) => setExportDepartmentIds(departmentIds)}
              departmentOptions={allDepartments}
              getItemLabel={(department) => {
                const counts = exportDepartmentParticipantCounts[department.id] || { direct: 0, total: 0 };
                return (
                  <span className="flex items-center justify-between gap-3">
                    <span className="truncate">{department.name}</span>
                    <span className="shrink-0 text-[11px] text-gray-500">
                      {exportDepartmentCountsLoading ? "..." : `${counts.total} katılımcı`}
                    </span>
                  </span>
                );
              }}
              multiple
            />
          </div>
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-xs text-gray-500">
              {exportDepartmentIds.length > 0 ? `${exportDepartmentIds.length} departman seçildi` : "Henüz departman seçilmedi"}
            </div>
            <div className="flex items-center gap-2">
              {allDepartments.length > 1 && (
                <button
                  type="button"
                  onClick={() => setExportDepartmentIds(allDepartments.map((department) => department.id))}
                  className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                  disabled={exportSubmitting}>
                  Tümünü Seç
                </button>
              )}
              <button
                type="button"
                onClick={() => setExportDepartmentIds([])}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
                disabled={exportSubmitting}>
                Temizle
              </button>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setShowExportModal(false);
                setExportSurvey(null);
                setExportDepartmentIds([]);
                setExportDepartmentParticipantCounts({});
                setExportDepartmentCountsLoading(false);
              }}
              disabled={exportSubmitting}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50">
              Vazgeç
            </button>
            <button
              type="button"
              onClick={handleExportConfirm}
              disabled={exportSubmitting || exportDepartmentIds.length === 0}
              className="px-4 py-2 text-sm rounded-lg bg-green-600 text-white hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {exportSubmitting ? "Dışa aktarılıyor..." : "Dışa Aktar"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMyAnswersModal}
        onClose={() => {
          setShowMyAnswersModal(false);
          setMyAnswersData(null);
        }}
        title={`${myAnswersData?.survey?.title || "Anket"} - Cevaplarım`}
        size="lg">
        {myAnswersLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
          </div>
        ) : (
          <div className="space-y-4">
            <div className="text-sm text-gray-500">
              {myAnswersData?.userSurvey?.completedAt ? `Tamamlanma: ${formatDate(myAnswersData.userSurvey.completedAt)}` : ""}
            </div>
            {Array.isArray(myAnswersData?.answers) && myAnswersData.answers.length > 0 ? (
              <div className="space-y-3">
                {myAnswersData.answers.map((answerItem: any, index: number) => (
                  <div key={answerItem.id || `${answerItem.question?.id}_${index}`} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start gap-3">
                      <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                        {index + 1}
                      </span>
                      <div className="flex-1">
                        <div className="font-medium text-gray-900 mb-1">{answerItem.question?.title || "-"}</div>
                        <div className="text-sm text-gray-700 bg-gray-50 rounded p-2">{renderMyAnswer(answerItem)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-500">Cevap bulunamadı.</div>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
